/**
 * ============================================================================
 * RAISE DISPUTE PAGE - 100% Ready
 * ============================================================================
 * 
 * Page for creating a new dispute
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserDashboardLayout } from '../components/user';
import { useDispute } from '../hooks/useDispute';
import { useTransaction } from '../hooks';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import RaiseIssue from '../components/dispute/RaiseIssue';
import Card from '../components/common/Card';
import Spinner from '../components/common/Spinner';
import Button from '../components/common/Button';

/**
 * Raise Dispute Page Component
 * @returns {JSX.Element}
 */
const RaiseDispute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { createDispute, loading } = useDispute();
  const { getTransaction } = useTransaction();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();

  const [transaction, setTransaction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const transactionId = new URLSearchParams(location.search).get('transactionId');

  useEffect(() => {
    const loadTransaction = async () => {
      if (!transactionId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getTransaction(transactionId);
        setTransaction(data);
      } catch (error) {
        showError(language === 'fr' ? 'Transaction introuvable' : 'Transaction not found');
        navigate('/transactions');
      } finally {
        setIsLoading(false);
      }
    };

    loadTransaction();
  }, [transactionId, getTransaction, showError, navigate, language]);

  const handleDisputeCreated = (dispute) => {
    showSuccess(
      language === 'fr'
        ? 'Litige créé avec succès!'
        : 'Dispute created successfully!'
    );
    navigate(`/disputes/${dispute.id || dispute.disputeId}`);
  };

  if (isLoading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/disputes')}
          className="mb-4"
        >
          ← {language === 'fr' ? 'Retour aux litiges' : 'Back to Disputes'}
        </Button>
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
          ⚠️ {language === 'fr' ? 'Créer un litige' : 'Raise a Dispute'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Décrivez votre problème et nous vous aiderons à le résoudre'
            : "Describe your issue and we'll help you resolve it"}
        </p>
      </div>

      {/* Transaction Info Card */}
      {transaction && (
        <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-2xl shadow-lg">
              💳
            </div>
            <div className="flex-1">
              <h3 className="font-black text-gray-900 dark:text-white mb-1">
                {transaction.title || transaction.description || (language === 'fr' ? 'Transaction' : 'Transaction')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Référence' : 'Reference'}: #{transaction.shortCode || transaction.id}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/transactions/${transaction.id}`)}
            >
              {language === 'fr' ? 'Voir' : 'View'} →
            </Button>
          </div>
        </Card>
      )}

      {/* Raise Issue Component */}
      <div className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        <RaiseIssue
          transactionId={transactionId || transaction?.id}
          transaction={transaction}
          onDisputeCreated={handleDisputeCreated}
        />
      </div>
    </UserDashboardLayout>
  );
};

export default RaiseDispute;
